// Shared location helpers for events. Both the above-the-fold "Open in Google
// Maps" link and the embedded EventMap derive their target from the event copy
// the same way, so the link and the map always point at the same pin as the
// WhatsApp share.

export type EventLocationLike = {
  body?: string | null;
  meeting_point?: string | null;
  location_address?: string | null;
  route_note?: string | null;
  // Explicit operator-set pin: an exact Google Maps URL (e.g. a maps.app.goo.gl
  // short link) and/or structured coordinates. When present these win over any
  // link/coords parsed out of the free-text copy.
  maps_uri?: string | null;
  lat?: number | null;
  lng?: number | null;
};

function haystack(event: EventLocationLike): string {
  return [
    event.body,
    event.meeting_point,
    event.location_address,
    event.route_note,
  ]
    .filter(Boolean)
    .join("\n");
}

/**
 * "lat,lng" for the event. Prefers the structured lat/lng columns (the exact
 * pin the operator dropped), then a "GPS: 51.4..., -0.1..." style line in the
 * copy. Returns null if neither is present.
 */
export function extractCoords(event: EventLocationLike): string | null {
  if (
    typeof event.lat === "number" &&
    typeof event.lng === "number" &&
    Number.isFinite(event.lat) &&
    Number.isFinite(event.lng)
  ) {
    return `${event.lat},${event.lng}`;
  }
  const m = haystack(event).match(
    /(-?\d{1,2}\.\d{4,})\s*,\s*(-?\d{1,3}\.\d{4,})/,
  );
  return m ? `${m[1]},${m[2]}` : null;
}

/**
 * A Google Maps URL for the event. Prefers an explicit Google Maps link in the
 * copy (the exact pin the operator dropped), then coordinates, then the
 * readable address. Returns null if there's nothing to point at.
 */
export function googleMapsUrl(event: EventLocationLike): string | null {
  // 1. Explicit operator-set pin (maps_uri column) wins over everything.
  const explicit = (event.maps_uri || "").trim();
  if (/^https?:\/\//i.test(explicit)) return explicit;

  // 2. A Google Maps link dropped inline in the copy.
  const link = haystack(event).match(
    /https?:\/\/(?:maps\.app\.goo\.gl|(?:www\.)?google\.[a-z.]+\/maps)[^\s)]+/i,
  );
  if (link) return link[0];

  const coords = extractCoords(event);
  if (coords) return `https://www.google.com/maps?q=${coords}`;

  const addr = event.location_address || event.meeting_point;
  return addr
    ? `https://www.google.com/maps?q=${encodeURIComponent(addr)}`
    : null;
}
